<script setup>
import { computed, nextTick, ref, watch, onMounted } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useToggle } from '@vueuse/core';
import Button from 'dashboard/components-next/button/Button.vue';
import MultiselectDropdownItems from 'shared/components/ui/MultiselectDropdownItems.vue';

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
  },
  selectedItems: {
    type: Array,
    default: () => [],
  },
  multiselectorTitle: {
    type: String,
    default: '',
  },
  multiselectorPlaceholder: {
    type: String,
    default: 'Selecionar...',
  },
  noSearchResult: {
    type: String,
    default: 'Nenhum resultado encontrado',
  },
  inputPlaceholder: {
    type: String,
    default: 'Buscar...',
  },
  icon: {
    type: String,
    default: '📊',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select']);

onMounted(() => {});

const [showSearchDropdown, toggleDropdown] = useToggle(false);

const buttonRef = ref(null);
const dropdownStyle = ref({});

const onCloseDropdown = () => {
  toggleDropdown(false);
};

const onClickSelectItem = value => {
  emit('select', value);
};

const hasValue = computed(() => {
  return props.selectedItems && props.selectedItems.length > 0;
});

const displayText = computed(() => {
  return props.multiselectorPlaceholder;
});

const updateDropdownPosition = () => {
  if (buttonRef.value) {
    const rect = buttonRef.value.getBoundingClientRect();

    dropdownStyle.value = {
      position: 'absolute',
      top: `${rect.bottom}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      minWidth: '220px',
      zIndex: 9999,
    };
  }
};

watch(
  () => props.options,
  async newOptions => {
    if (newOptions) {
      await nextTick();
      updateDropdownPosition();
    }
  },
  { immediate: true }
);

watch(showSearchDropdown, async isVisible => {
  if (isVisible) {
    await nextTick();
    updateDropdownPosition();

    const handleClickOutside = event => {
      if (buttonRef.value && !buttonRef.value.contains(event.target)) {
        const dropdown = document.querySelector('.dropdown-debug');
        if (dropdown && !dropdown.contains(event.target)) {
          onCloseDropdown();
        }
      }
    };

    document.addEventListener('click', handleClickOutside);

    const scrollableContainer = buttonRef.value?.closest('.overflow-x-auto');
    if (scrollableContainer) {
      const handleScroll = () => {
        if (showSearchDropdown.value) {
          updateDropdownPosition();
        }
      };

      scrollableContainer.addEventListener('scroll', handleScroll);

      const unwatch = watch(showSearchDropdown, isOpen => {
        if (!isOpen) {
          scrollableContainer.removeEventListener('scroll', handleScroll);
          document.removeEventListener('click', handleClickOutside);
          unwatch();
        }
      });
    }
  }
});
</script>

<template>
  <div ref="buttonRef" class="relative w-full" @keyup.esc="onCloseDropdown">
    <Button
      slate
      outline
      trailing-icon
      :icon="
        showSearchDropdown ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
      "
      class="w-full !px-3 !py-2 min-h-[36px]"
      :disabled="disabled"
      @click="() => toggleDropdown()"
    >
      <div class="flex items-center justify-between w-full min-w-0">
        <div class="flex items-center space-x-2">
          <h4 class="text-sm text-ellipsis text-n-slate-12">
            {{ displayText }}
          </h4>
          <span
            v-if="hasValue"
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
          >
            {{ selectedItems.length }}
          </span>
        </div>
      </div>
    </Button>
    <teleport to="body">
      <div
        v-if="showSearchDropdown"
        :style="dropdownStyle"
        class="dropdown-debug box-border top-[2.5rem] w-full min-w-[220px] border rounded-lg bg-n-alpha-3 backdrop-blur-[100px] absolute shadow-lg border-n-strong dark:border-n-strong p-3 z-[9999]"
      >
        <div class="flex items-center justify-between mb-1">
          <h4
            class="m-0 overflow-hidden text-sm text-n-slate-11 whitespace-nowrap text-ellipsis"
          >
            {{ multiselectorTitle }}
          </h4>
          <Button ghost slate xs icon="i-lucide-x" @click="onCloseDropdown" />
        </div>

        <MultiselectDropdownItems
          v-if="showSearchDropdown"
          :options="options"
          :selected-items="selectedItems"
          :has-thumbnail="false"
          :input-placeholder="inputPlaceholder"
          :no-search-result="noSearchResult"
          @select="onClickSelectItem"
        />
      </div>
    </teleport>
  </div>
</template>
